import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import usePagination from '../../hooks/usePagination';
import PaymentInfo from './PaymentInfo';
import Transactions from './Transactions';
import { getViewersTimeZone } from '../../utils';
import './StorePayments.css';

interface StorePaymentsWrapperProps {
  storeId: number;
  onStoreName?: (storeName: string) => void;
}

const StorePaymentsWrapper: React.FC<StorePaymentsWrapperProps> = ({ storeId, onStoreName }) => {
  const dateFilterRef = useRef<{ from?: string; to?: string }>({});

  const {
    data: history,
    setData: setHistory,
    responseMeta: localData,
    setResponseMeta: setLocalData,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    errorContent,
    fetchInitial,
    fetchMore,
  } = usePagination({ url: `${import.meta.env.VITE_API_URL}/payments/transaction-history`, method: 'POST', listKey: 'history'});

  useEffect(() => {
    dateFilterRef.current = {};
    fetchInitial({ store_id: storeId });
  }, [storeId, fetchInitial]);

  const resolvedStoreName = useMemo(() => {
    const topLevel: unknown = localData?.store_name;
    return typeof topLevel === 'string' ? topLevel.trim() : '';
  }, [localData]);

  useEffect(() => {
    if (resolvedStoreName) {
      onStoreName?.(resolvedStoreName);
    }
  }, [resolvedStoreName, onStoreName]);

  const handleApplyDateFilter = useCallback((from?: string, to?: string) => {
    const filter: { from?: string; to?: string; timezone?: string } = {};
    if (from) filter.from = from;
    if (to) filter.to = to;
    filter.timezone = getViewersTimeZone();
    dateFilterRef.current = filter;
    fetchInitial({ store_id: storeId, ...filter });
  }, [storeId, fetchInitial]);

  const handleClearDateFilter = useCallback(() => {
    dateFilterRef.current = {};
    fetchInitial({ store_id: storeId });
  }, [storeId, fetchInitial]);

  const fetchMoreTransactions = useCallback(() => {
    fetchMore({ store_id: storeId, ...dateFilterRef.current });
  }, [storeId, fetchMore]);

  useEffect(() => {
    const handlePaymentSettingsUpdate = (e: any) => {
      const updatedData = e.detail;
      setLocalData((prev: any) => {
        if (!prev) return prev;
        
        const newPaymentInfo = { ...prev.paymentInfo };
        if (updatedData?.store_payment_method) {
          newPaymentInfo.store_payment_method = updatedData.store_payment_method;
        }
        if (updatedData?.checkDetails) {
          newPaymentInfo.check_details = {
            ...newPaymentInfo.check_details,
            ...updatedData.checkDetails
          };
        }
        
        return { ...prev, paymentInfo: newPaymentInfo };
      });
    };

    window.addEventListener('paymentSettingsUpdated', handlePaymentSettingsUpdate);

    return () => {
      window.removeEventListener('paymentSettingsUpdated', handlePaymentSettingsUpdate);
    };
  }, [setLocalData]);

  const handlePaymentSuccess = (newTransaction: any) => {
    if (localData) {
      const apiBalance = newTransaction.balance_after !== undefined ? newTransaction.balance_after : newTransaction.balance_after;
      const finalBalance = apiBalance !== undefined ? Number(apiBalance).toFixed(2) : (Number(localData.current_balance) - Number(newTransaction.amount)).toFixed(2);
      
      const updatedTransaction = {
        ...newTransaction,
        balance_after: apiBalance
      };

      setHistory((prev) => [updatedTransaction, ...prev]);

    setLocalData((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        total_out: (Number(prev.total_out) + Number(newTransaction.amount)),
        current_balance: finalBalance
      };
      });
    }
  };

  return (
    <div className="store-payments-wrapper">
      {errorContent && <div className="error-alert">{errorContent}</div>}
      
      {isLoading && !localData ? (
        <p>Loading payment information...</p>
      ) : error?.code === 401 ? (
        null
      ) : localData ? (
        <>
          <PaymentInfo 
            storeId={storeId} 
            paymentInfo={localData.paymentInfo} 
            onPaymentSuccess={handlePaymentSuccess} 
          />
          <Transactions 
            storeId={storeId}
            history={history || []} 
            total_earned={localData.total_earned || 0} 
            total_refunded ={localData.total_refunded || 0}
            total_out={localData.total_out || 0} 
            currentBalance={localData.current_balance || 0}
            payment_state={localData.payment_state}
            paymentMethod={localData.paymentInfo?.store_payment_method}
            onPaymentSuccess={handlePaymentSuccess}
            onLoadMore={fetchMoreTransactions}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            onApplyDateFilter={handleApplyDateFilter}
            onClearDateFilter={handleClearDateFilter}
          />
        </>
      ) : !isLoading && !errorContent ? (
        <p>No payment data found for this store.</p>
      ) : null}
    </div>
  );
};

export default StorePaymentsWrapper;
